import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowUpRight,
  Building2,
  ClipboardList,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Truck,
  UserRound,
  Users,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';

const accionesAdministrativas = [
  {
    key: 'clientes',
    title: 'Clientes',
    description: 'Altas, bajas y edición de la cartera comercial.',
    icon: Building2,
    to: '/clientes',
  },
  {
    key: 'unidades',
    title: 'Vehículos / Flota',
    description: 'Unidades, disponibilidad y documentación.',
    icon: Truck,
    to: '/administracion/vehiculos',
  },
  {
    key: 'choferes',
    title: 'Choferes',
    description: 'Asignaciones, licencias y control de actividad.',
    icon: UserRound,
    to: '/administracion/choferes',
  },
  {
    key: 'usuarios',
    title: 'Usuarios',
    description: 'Gestión de accesos y perfiles internos.',
    icon: Users,
    to: '/seguridad/usuarios',
  },
  {
    key: 'proveedores',
    title: 'Proveedores',
    description: 'Alta y seguimiento de proveedores estratégicos.',
    icon: ClipboardList,
    to: '/admin/proveedores',
  },
  {
    key: 'tarifas',
    title: 'Tarifas',
    description: 'Matrices tarifarias por cliente y zona.',
    icon: SlidersHorizontal,
    to: '/tarifas',
  },
];

const nuevosAccesos = [
  { label: 'Cliente', to: '/clientes/nuevo' },
  { label: 'Proveedor', to: '/admin/proveedores/nuevo' },
  { label: 'Vehículo', to: '/administracion/vehiculos/nuevo' },
  { label: 'Chofer', to: '/administracion/choferes/nuevo' },
  { label: 'Usuario', to: '/seguridad/usuarios/nuevo' },
  { label: 'Tarifa', to: '/tarifas' },
];

// El dashboard anterior sólo exponía valores simulados. Hasta conectar una fuente
// existente y confiable, el diseño comunica la ausencia de dato en vez de inventarlo.
const dashboardData = null;

function KpiCard({ title, to, data }) {
  const value = data?.activos;
  const total = data?.total;

  return (
    <Link
      to={to}
      className="group flex min-h-[142px] flex-col rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.84)] p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[rgba(var(--brand),0.45)] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
    >
      <p className="text-3xl font-semibold leading-none tracking-tight text-[rgb(var(--fg))]">
        {Number.isFinite(value) ? value.toLocaleString('es-AR') : '—'}
      </p>
      <p className="mt-2 text-sm font-semibold text-[rgb(var(--fg))]">{title}</p>
      <p className="mt-1 text-xs text-[rgb(var(--muted))]">
        {Number.isFinite(total) ? `${total.toLocaleString('es-AR')} registrados en total` : 'Datos no disponibles'}
      </p>
      <span className="mt-auto flex items-center gap-1 pt-3 text-xs font-medium text-[rgb(var(--brand))]">
        Ver listado <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

function EmptyAttention() {
  return (
    <div className="flex min-h-[108px] items-center gap-3 rounded-xl border border-dashed border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.38)] px-4 py-4 text-sm text-[rgb(var(--muted))]">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[rgba(var(--brand),0.1)] text-[rgb(var(--brand-700))]">
        <ShieldCheck className="h-4 w-4" aria-hidden="true" />
      </span>
      <div>
        <p className="font-medium text-[rgb(var(--fg))]">No hay alertas administrativas pendientes.</p>
        <p className="mt-0.5 text-xs">Las alertas se mostrarán cuando haya datos confiables disponibles.</p>
      </div>
    </div>
  );
}

export default function DashboardAdministracion() {
  const kpiCards = [
    { key: 'clientes', title: 'Clientes activos', to: '/clientes?activos=1' },
    { key: 'choferes', title: 'Choferes activos', to: '/administracion/choferes?activos=1' },
    { key: 'unidades', title: 'Unidades activas', to: '/administracion/vehiculos?activos=1' },
    { key: 'usuarios', title: 'Usuarios activos', to: '/seguridad/usuarios?activos=1' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-5 px-4 pb-10 pt-5 sm:px-6">
      <PageHeader
        title="Administración"
        subtitle="Gestión de clientes, flota, choferes, usuarios y parámetros del sistema."
        actions={(
          <details className="group relative w-full sm:w-auto">
            <summary className="flex h-10 cursor-pointer list-none items-center justify-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] [&::-webkit-details-marker]:hidden">
              <span aria-hidden="true">+</span> Nuevo
            </summary>
            <div className="absolute right-0 z-20 mt-2 grid w-full min-w-[190px] overflow-hidden rounded-xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.98)] p-1.5 shadow-lg sm:w-52">
              {nuevosAccesos.map((action) => (
                <Link
                  key={action.label}
                  to={action.to}
                  className="rounded-lg px-3 py-2 text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {action.label}
                </Link>
              ))}
            </div>
          </details>
        )}
      />

      <section aria-labelledby="resumen-administrativo">
        <div className="mb-3">
          <h2 id="resumen-administrativo" className="text-base font-semibold text-[rgb(var(--fg))]">Resumen administrativo</h2>
          <p className="mt-0.5 text-sm text-[rgb(var(--muted))]">Estado actual de los principales maestros del sistema.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {kpiCards.map((card) => (
            <KpiCard key={card.key} title={card.title} to={card.to} data={dashboardData?.[card.key]} />
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.72)] p-4 shadow-sm sm:p-5" aria-labelledby="requiere-atencion">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[rgba(var(--brand),0.1)] text-[rgb(var(--brand-700))]">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <h2 id="requiere-atencion" className="text-base font-semibold text-[rgb(var(--fg))]">Requiere atención</h2>
            <p className="mt-0.5 text-sm text-[rgb(var(--muted))]">Vencimientos e incidencias que requieren revisión.</p>
          </div>
        </div>
        <div className="mt-4"><EmptyAttention /></div>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.72)] p-4 shadow-sm sm:p-5" aria-labelledby="gestion-administrativa">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[rgba(var(--brand),0.1)] text-[rgb(var(--brand-700))]">
            <Settings className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <h2 id="gestion-administrativa" className="text-base font-semibold text-[rgb(var(--fg))]">Gestión administrativa</h2>
            <p className="mt-0.5 text-sm text-[rgb(var(--muted))]">Accedé a los principales maestros y configuraciones del sistema.</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {accionesAdministrativas.map((item) => {
            const Icon = item.icon;
            const data = dashboardData?.[item.key];
            const total = data?.total;
            const activos = data?.activos;
            return (
              <Link
                key={item.key}
                to={item.to}
                className="group flex min-h-[132px] flex-col rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.88)] p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[rgba(var(--brand),0.45)] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--brand),0.1)] text-[rgb(var(--brand-700))]">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">{item.title}</h3>
                    <p className="mt-1 line-clamp-2 text-xs leading-5 text-[rgb(var(--muted))]">{item.description}</p>
                  </div>
                </div>
                <div className="mt-auto flex items-center justify-between gap-3 pt-3 text-xs">
                  <span className="text-[rgb(var(--muted))]">
                    {Number.isFinite(total) ? `${total.toLocaleString('es-AR')} registrados · ${activos.toLocaleString('es-AR')} activos` : 'Datos no disponibles'}
                  </span>
                  <span className="flex shrink-0 items-center gap-1 font-medium text-[rgb(var(--brand))]">
                    Administrar <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
